import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import beanMascot from "@/assets/bean-mascot.svg";

const budgetTiers = [
  { value: "Under $2.5K", label: "Under $2.5K", vibe: "Just getting started" },
  { value: "$2.5K – $5K", label: "$2.5K – $5K", vibe: "A little something" },
  { value: "$5K – $8K", label: "$5K – $8K", vibe: "We're serious" },
  { value: "$8K – $12K", label: "$8K – $12K", vibe: "Let's build something real" },
  { value: "$12K+", label: "$12K+", vibe: "Go big or go home" },
  { value: "Flexible", label: "Flexible", vibe: "Let's figure it out" },
];

const timelineOptions = ["ASAP", "1–3 months", "3–6 months", "6+ months", "9+ months", "Flexible"];

interface StepBoringStuffProps {
  budget: string;
  timeline: string;
  update: (field: string, value: string) => void;
  isDallasLocal: boolean;
  dallasOfferType: string;
  tradeOffer: string;
  setTradeOffer: (v: string) => void;
}

const StepBoringStuff = ({
  budget,
  timeline,
  update,
  isDallasLocal,
  dallasOfferType,
  tradeOffer,
  setTradeOffer,
}: StepBoringStuffProps) => {
  const showBudget = !isDallasLocal || dallasOfferType === "30% Discount";
  const showTradeOffer = isDallasLocal && dallasOfferType === "Barter / Trade";
  const [stamped, setStamped] = useState<string | null>(null);

  const handleBudgetSelect = (value: string) => {
    const next = budget === value ? "" : value;
    update("budget", next);
    if (next) {
      setStamped(next);
      setTimeout(() => setStamped(null), 600);
    }
  };

  

  return (
    <div className="flex flex-col gap-6 px-6 py-5">
      <div>
        <p className="font-heading text-ts-base text-foreground mb-1">
          The boring stuff
        </p>
        <p className="font-body text-ts-xs text-foreground/60">
          Almost there — just a couple quick picks.
        </p>
      </div>

      {showTradeOffer ? (
        <div>
          <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
            Trade offer
          </label>
          <p className="font-body text-ts-2xs text-foreground/50 mb-3">
            Tell us what you'd like to trade — e.g. "We run a photography business. Build our site, you get a free 60 min session."
          </p>
          <textarea
            value={tradeOffer}
            onChange={(e) => setTradeOffer(e.target.value)}
            maxLength={500}
            rows={4}
            className="w-full bg-transparent border border-border p-3 font-body text-ts-xs text-foreground placeholder:text-foreground/30 focus:border-primary focus:outline-none transition-colors resize-none"
            placeholder="Describe your trade offer..."
          />
        </div>
      ) : showBudget ? (
        <div className="relative">
          <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
            Budget range
          </label>

          {/* Budget pills */}
          <div className="flex flex-wrap gap-1.5">
            {budgetTiers.map((tier) => (
              <button
                key={tier.value}
                type="button"
                onClick={() => handleBudgetSelect(tier.value)}
                className={`relative px-3 py-1.5 border font-body text-ts-2xs tracking-wide transition-all duration-200 ${
                  budget === tier.value
                    ? "bg-primary text-primary-foreground border-primary scale-[1.03]"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                }`}
              >
                {tier.label}
              </button>
            ))}
          </div>


          {/* Bean mascot stamp effect */}
          <AnimatePresence>
            {stamped && (
              <motion.div
                key={stamped}
                initial={{ opacity: 0, scale: 2.5, rotate: -15 }}
                animate={{ opacity: 0.12, scale: 1, rotate: -8 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ 
                  duration: 0.35, 
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="absolute -right-2 -top-2 pointer-events-none select-none"
              >
                <img
                  src={beanMascot}
                  alt=""
                  className="w-20 h-20 dark:invert"
                  aria-hidden="true"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : null}

      <div>
        <label className="font-body text-ts-2xs font-medium tracking-[0.1em] text-muted-foreground mb-2 block">
          Timeline
        </label>
        <div className="flex flex-wrap gap-1.5">
          {timelineOptions.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => update("timeline", timeline === opt ? "" : opt)}
              className={`px-3 py-1.5 border font-body text-ts-2xs tracking-wide transition-colors ${
                timeline === opt
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StepBoringStuff;
